import { EmailStep } from '@features/web-auth/ui/EmailStep'
import { CodeStep } from '@features/web-auth/ui/CodeStep'
import { Modal } from '@shared/ui/Modal'
import { Button } from '@shared/ui/Button'
import { ENV } from '@shared/config/env'
import { useLinkEmailFlow } from '../model/useLinkEmailFlow'
import styles from './LinkEmailModal.module.css'

interface LinkEmailModalProps {
  /** Колбэк закрытия модалки */
  onClose: () => void
}

/**
 * Модалка привязки email в TG-версии: email → (will_merge) → code → done/taken.
 * Вся бизнес-логика — в useLinkEmailFlow; здесь только композиция экранов.
 * В TG-сборке рендерится как bottom-sheet, в web — по центру.
 * @param onClose - Колбэк закрытия модалки
 */
export const LinkEmailModal = ({ onClose }: LinkEmailModalProps) => {
  const flow = useLinkEmailFlow({ onClose })
  const variant = ENV.IS_TG ? 'bottomSheet' : 'center'

  return (
    <Modal onClose={onClose} variant={variant} showClose>
      {ENV.IS_DEV && flow.debugCode && (
        <div className={styles.debug}>debug code: {flow.debugCode}</div>
      )}

      <div className={styles.content}>
        {flow.step === 'email' && (
          <EmailStep
            title="Привязать email"
            submitLabel="Получить код"
            showAgreement
            onSubmit={flow.submitEmail}
            isSubmitting={flow.submitting}
            errorText={flow.error}
          />
        )}

        {flow.step === 'will-merge-confirm' && (
          <div className={styles.panel}>
            <h3 className={styles.title}>На этой почте уже есть аккаунт</h3>
            <p className={styles.text}>
              Если вы подтвердите привязку, аккаунты объединятся: подписка, прогресс,
              избранное и другие данные будут слиты в один аккаунт.
            </p>
            <Button onClick={flow.confirmMerge} block>Продолжить</Button>
            <Button variant="link" onClick={onClose} block>Отмена</Button>
          </div>
        )}

        {flow.step === 'code' && (
          <CodeStep
            email={flow.email}
            onSubmit={flow.submitCode}
            onResend={flow.resendCode}
            onChangeEmail={flow.backToEmail}
            errorText={flow.error}
            attemptsLeft={flow.attemptsLeft}
            isLocked={flow.isLocked}
            nextResendAt={flow.nextResendAt}
            isSubmitting={flow.submitting}
            shakeSignal={flow.shakeSignal}
          />
        )}

        {flow.step === 'done' && (
          <div className={styles.panel}>
            <h3 className={styles.title}>Email привязан</h3>
            <Button onClick={onClose} block>Готово</Button>
          </div>
        )}

        {flow.step === 'email-taken' && (
          <div className={styles.panel}>
            <h3 className={styles.title}>Email занят</h3>
            <p className={styles.text}>
              Этот email используется другим аккаунтом Telegram. Используйте другой email.
            </p>
            <Button onClick={flow.backToEmail} block>Указать другой email</Button>
            <Button variant="link" onClick={onClose} block>Закрыть</Button>
          </div>
        )}
      </div>
    </Modal>
  )
}
